import { addWeeks, format, isAfter } from 'date-fns'
import uniq from 'lodash-es/uniq'

import {
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_INTERNAL_SERVER_ERROR as HTTP_STATUS_ERROR,
  HTTP_STATUS_OK,
} from '../../../constants/api'
import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import { DEFAULT_TIME_ZONE } from '../../../constants/date'
import {
  DATE_FILTER_TYPE,
  SELECT_FILTER_TYPE,
  TOGGLE_FILTER_TYPE,
} from '../../../constants/filter'
import {
  RENTAL_PORTAL_VIEWING_BOOKED_STATUSES,
  RENTAL_PORTAL_VIEWING_OUTCOME_STATUSES,
  RENTAL_PORTAL_VIEWING_STATUS,
  RENTAL_PORTAL_VIEWING_STATUS_SLOT_OPEN,
  RENTAL_PORTAL_VIEWING_STATUSES,
} from '../../../constants/rentalPortal'
import {
  RENTAL_PORTAL_MOCK_LATENCY,
  RENTAL_PORTAL_MOCK_STATE,
} from '../../../constants/rentalPortalMockState'
import { DESC_ORDER_SYMBOL } from '../../../constants/sort'
import { isApiSuccessStatus } from '../../../utils/api'
import { toUkTimezoneDate } from '../../../utils/date'
import {
  APPLICANTS_SNAPSHOT,
  LISTINGS_SNAPSHOT,
  VIEWINGS_SNAPSHOT,
} from '../../../utils/rentalPortalMockData'
import { getRentalPortalMockAvatarColor } from '../../../utils/rentalPortalMockMedia'
import {
  hasRentalPortalMockCreateRight,
  hasRentalPortalMockState,
} from '../../../utils/rentalPortalMockState'
import { toMockApplicantReference } from '../../RentalPortalApplicant'
import {
  VIEWING_COLUMN_NAME,
  VIEWING_MASS_ACTION,
  VIEWINGS_FILTER_DATE_NAME,
  VIEWINGS_FILTER_PARAM,
} from '../RentalPortalViewing.constants'
import {
  getIsViewingPast,
  getViewingsColumns,
} from '../RentalPortalViewing.utils'

const MOCK_VIEWINGS_READ_LATENCY = 300

const MINUTES_IN_MS = 60000

const MOCK_VIEWINGS_MANAGER_LABEL = 'N. Kaminskii'

const MOCK_VIEWINGS_MANAGER_AVATAR_COLOR = Object.fromEntries(
  uniq(VIEWINGS_SNAPSHOT.map(viewing => viewing.manager_label))
    .sort()
    .map((label, index) => [label, getRentalPortalMockAvatarColor(index)])
)

const MOCK_VIEWING_BOOKABLE_STAGES = ['approved', 'screening', 'selected']

const MOCK_VIEWING_SLOT_TAKEN_ERROR = { isSlotTaken: true }

const MOCK_VIEWING_STATUS_CONFIRMED = 'confirmed'

const MOCK_VIEWING_NOTE_BY_ID = new Map()

const MOCK_VIEWING_OUTCOME_BY_ID = new Map()

// TODO: stands in for the additional attendees of `GET …/viewings/{id}/` — the
// booked applicant stays `applicant`, the extra ones live here until the
// endpoint carries an `applicants` list (EPIC-RP-06, handover).
// Seeded so a viewing with several applicants exists on load; the widget and
// the booking form add to this map at runtime
const MOCK_VIEWING_EXTRA_APPLICANT_IDS_BY_ID = new Map([
  [1, [6, 28]],
  [10, [5]],
])

const MOCK_VIEWING_OUTCOME_ACTOR = 'N. Kaminskii'

const MOCK_VIEWING_ICS_MIME = 'text/calendar;charset=utf-8'

const MOCK_VIEWING_ICS_DATE_FORMAT = "yyyyMMdd'T'HHmmss"

const MOCK_VIEWING_ICS_STAMP_FORMAT = "yyyyMMdd'T'HHmmss'Z'"

const MOCK_VIEWING_ICS_STAMP_TIMEZONE = 'UTC'

const MOCK_VIEWING_ICS_LINE_BREAK = '\r\n'

const MOCK_VIEWINGS_READ_ERROR = { response: { status: HTTP_STATUS_ERROR } }

const MOCK_VIEWINGS_WRITE_ERROR = { response: { status: HTTP_STATUS_ERROR } }

export const MOCK_VIEWINGS_PERMISSIONS = {
  can_create: hasRentalPortalMockCreateRight(),
  can_export: true,
}

const MOCK_VIEWINGS_DEFAULT_ORDERING = [
  `${DESC_ORDER_SYMBOL}${VIEWING_COLUMN_NAME.DateTime}`,
]

function withMockLatency(delay) {
  return new Promise(resolve => {
    setTimeout(resolve, delay)
  })
}

function getMockReadDelay(isInitial) {
  const state = isInitial
    ? RENTAL_PORTAL_MOCK_STATE.loading
    : RENTAL_PORTAL_MOCK_STATE.slowRefresh

  return hasRentalPortalMockState(state)
    ? RENTAL_PORTAL_MOCK_LATENCY
    : MOCK_VIEWINGS_READ_LATENCY
}

function getIsMockReadFailing(isInitial) {
  return (
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error) ||
    (!isInitial &&
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.refreshError))
  )
}

function compareViewingValues(left, right) {
  if (left == null && right == null) {
    return 0
  }

  if (left == null) {
    return -1
  }

  if (right == null) {
    return 1
  }

  return String(left).localeCompare(String(right))
}

function getSortedMockViewings(viewings, ordering) {
  const orderingList =
    ordering?.length > 0 ? ordering : MOCK_VIEWINGS_DEFAULT_ORDERING

  return [...viewings].sort((left, right) => {
    for (const entry of orderingList) {
      const isDesc = entry.startsWith(DESC_ORDER_SYMBOL)
      const field = isDesc ? entry.slice(DESC_ORDER_SYMBOL.length) : entry

      const result = compareViewingValues(left[field], right[field])

      if (result !== 0) {
        return isDesc ? -result : result
      }
    }

    return 0
  })
}

const MOCK_VIEWING_LISTING_FLAT = new Map(
  LISTINGS_SNAPSHOT.map(listing => [listing.id, listing.flat])
)

const MOCK_VIEWINGS = VIEWINGS_SNAPSHOT.map(viewing => ({
  ...viewing,
  flat: MOCK_VIEWING_LISTING_FLAT.get(viewing.listing_id) ?? null,
}))

function getMockActiveViewings() {
  return MOCK_VIEWINGS.filter(viewing => !viewing.archived)
}

let mockMixedEligibilityViewings = null

function getMockMixedEligibilityViewings() {
  mockMixedEligibilityViewings ??= getMockActiveViewings().map(
    (viewing, index) =>
      index % 2 === 1
        ? {
            ...viewing,
            permissions: {
              ...viewing.permissions,
              can_archive: false,
              can_restore: false,
            },
          }
        : viewing
  )

  return mockMixedEligibilityViewings
}

function toMockServedViewing(viewing) {
  return {
    ...viewing,
    applicant: viewing.applicant
      ? {
          ...viewing.applicant,
          avatar_color: getRentalPortalMockAvatarColor(viewing.applicant.id),
          reference:
            viewing.applicant.masked === false
              ? null
              : toMockApplicantReference(viewing.applicant.label),
        }
      : null,
    manager_avatar_color:
      MOCK_VIEWINGS_MANAGER_AVATAR_COLOR[viewing.manager_label] ?? null,
  }
}

function getMockViewingsRows() {
  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)) {
    return EMPTY_LIST
  }

  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.massActionMixed)) {
    return getMockMixedEligibilityViewings()
  }

  return getMockActiveViewings()
}

function getMockViewingsSource() {
  return getMockViewingsRows().map(toMockServedViewing)
}

function toMockViewingFilterOptions(entries) {
  return [...new Map(entries).entries()]
    .map(([value, label]) => ({ label, value }))
    .sort((left, right) =>
      String(left.label).localeCompare(String(right.label))
    )
}

const MOCK_VIEWING_LISTING_OPTIONS = toMockViewingFilterOptions(
  MOCK_VIEWINGS.map(viewing => [viewing.listing_id, viewing.listing_label])
)

const MOCK_VIEWING_MANAGER_OPTIONS = [
  ...new Set(MOCK_VIEWINGS.map(viewing => viewing.manager_label)),
]
  .sort((left, right) => left.localeCompare(right))
  .map(label => ({
    label: {
      avatar_color: MOCK_VIEWINGS_MANAGER_AVATAR_COLOR[label],
      fullname: label,
      id: label,
    },
    value: label,
  }))

const MOCK_VIEWING_LISTING_OWNER_BY_ID = new Map(
  LISTINGS_SNAPSHOT.map(listing => [listing.id, listing.responsible_user])
)

const MOCK_VIEWING_MANAGER_SELECT_OPTIONS = [
  ...new Map(
    LISTINGS_SNAPSHOT.filter(listing => listing.responsible_user).map(
      listing => [
        listing.responsible_user.id,
        listing.responsible_user.full_name,
      ]
    )
  ).entries(),
].map(([value, label]) => ({ label, value }))

export const MOCK_VIEWINGS_AVAILABLE_FILTERS = [
  {
    name: VIEWINGS_FILTER_PARAM.booked,
    query_param: VIEWINGS_FILTER_PARAM.booked,
    title: 'RentalPortal.ViewingStatusBookedKey',
    type: TOGGLE_FILTER_TYPE,
  },
  {
    name: VIEWINGS_FILTER_PARAM.responsibleUser,
    options: MOCK_VIEWING_MANAGER_SELECT_OPTIONS,
    placeholder: 'RentalPortal.ListingProfileResponsibleManagerPlaceholderKey',
    query_param: VIEWINGS_FILTER_PARAM.responsibleUser,
    title: 'RentalPortal.ListingProfileResponsibleManagerKey',
    type: SELECT_FILTER_TYPE,
  },
  {
    name: VIEWINGS_FILTER_PARAM.listing,
    options: MOCK_VIEWING_LISTING_OPTIONS,
    placeholder: 'RentalPortal.ApplicantListingFilterPlaceholderKey',
    query_param: VIEWINGS_FILTER_PARAM.listing,
    title: 'RentalPortal.ViewingListingColumnKey',
    type: SELECT_FILTER_TYPE,
  },
  {
    name: VIEWINGS_FILTER_PARAM.status,
    options: Object.entries(RENTAL_PORTAL_VIEWING_STATUSES).map(
      ([status, { text }]) => ({
        label_key: text,
        value: status,
      })
    ),
    placeholder: 'RentalPortal.ListingStatusFilterPlaceholderKey',
    query_param: VIEWINGS_FILTER_PARAM.status,
    title: 'RentalPortal.ViewingStatusColumnKey',
    type: SELECT_FILTER_TYPE,
  },
  {
    name: VIEWINGS_FILTER_DATE_NAME,
    placeholder: 'RentalPortal.ViewingDateRangeFilterPlaceholderKey',
    query_param: [
      VIEWINGS_FILTER_PARAM.startGte,
      VIEWINGS_FILTER_PARAM.startLte,
    ],
    title: 'RentalPortal.ViewingDateTimeColumnKey',
    type: DATE_FILTER_TYPE,
  },
  {
    name: VIEWINGS_FILTER_PARAM.manager,
    options: MOCK_VIEWING_MANAGER_OPTIONS,
    placeholder: 'RentalPortal.ListingProfileResponsibleManagerPlaceholderKey',
    query_param: VIEWINGS_FILTER_PARAM.manager,
    title: 'RentalPortal.ViewingManagerColumnKey',
    type: SELECT_FILTER_TYPE,
  },
]

const MOCK_VIEWING_FACET_MATCH = {
  [VIEWINGS_FILTER_PARAM.booked]: viewing =>
    RENTAL_PORTAL_VIEWING_BOOKED_STATUSES.includes(viewing.status),
  [VIEWINGS_FILTER_PARAM.listing]: (viewing, value) =>
    String(viewing.listing_id) === String(value),
  [VIEWINGS_FILTER_PARAM.manager]: (viewing, value) =>
    viewing.manager_label === value,
  [VIEWINGS_FILTER_PARAM.responsibleUser]: (viewing, value) =>
    String(MOCK_VIEWING_LISTING_OWNER_BY_ID.get(viewing.listing_id)?.id) ===
    String(value),
  [VIEWINGS_FILTER_PARAM.startGte]: (viewing, value) =>
    viewing.start.slice(0, 10) >= value,
  [VIEWINGS_FILTER_PARAM.startLte]: (viewing, value) =>
    viewing.start.slice(0, 10) <= value,
  [VIEWINGS_FILTER_PARAM.status]: (viewing, value) => viewing.status === value,
}

function getFilteredMockViewings(viewings, filterValues) {
  return viewings.filter(viewing =>
    Object.entries(filterValues ?? EMPTY_OBJECT).every(
      ([param, value]) =>
        !value ||
        !MOCK_VIEWING_FACET_MATCH[param] ||
        MOCK_VIEWING_FACET_MATCH[param](viewing, value)
    )
  )
}

function getPagedMockViewings(viewings, { page, pageSize }) {
  const firstIndex = (page - 1) * pageSize

  return viewings.slice(firstIndex, firstIndex + pageSize)
}

function toMockBookedViewing({ end, listingId, start }) {
  return {
    applicant: null,
    applicant_label: null,
    archived: false,
    archived_at: null,
    booked_at: new Date().toISOString(),
    duration_min: Math.round(
      (new Date(end).getTime() - new Date(start).getTime()) / MINUTES_IN_MS
    ),
    end,
    flat: MOCK_VIEWING_LISTING_FLAT.get(listingId) ?? null,
    id: Math.max(...MOCK_VIEWINGS.map(viewing => viewing.id)) + 1,
    listing_id: listingId,
    listing_label: MOCK_VIEWING_LISTING_LABEL.get(listingId) ?? null,
    manager_label: MOCK_VIEWINGS_MANAGER_LABEL,
    permissions: {
      can_archive: true,
      can_manage: true,
      can_restore: false,
      can_view: true,
    },
    source: 'manager',
    start,
    status: MOCK_VIEWING_STATUS_CONFIRMED,
    uk_id: 1,
  }
}

function getIsMockViewingApplicantBookable(applicant) {
  return (
    MOCK_VIEWING_BOOKABLE_STAGES.includes(applicant.stage) &&
    applicant.identity?.masked === false &&
    Boolean(applicant.identity.value) &&
    !applicant.archived
  )
}

function hasMockViewingApplicantMatch(applicant, search) {
  return applicant.identity.value
    .toLowerCase()
    .includes(search.trim().toLowerCase())
}

export function getMockViewingApplicants({
  exclude_applicant_id: excludeApplicantId,
  listing_id: listingId,
  search,
}) {
  return withMockLatency(getMockReadDelay(true)).then(() => {
    if (getIsMockReadFailing(false)) {
      return Promise.reject(MOCK_VIEWINGS_READ_ERROR)
    }

    const items = APPLICANTS_SNAPSHOT.filter(
      applicant =>
        String(applicant.listing_id) === String(listingId) &&
        String(applicant.id) !== String(excludeApplicantId) &&
        getIsMockViewingApplicantBookable(applicant) &&
        (!search || hasMockViewingApplicantMatch(applicant, search))
    )

    return {
      data: {
        items: items.map(applicant => ({
          ...applicant,
          avatar_color: getRentalPortalMockAvatarColor(applicant.id),
        })),
        meta: { count: items.length, curr_page: 1, page_count: 1 },
      },
    }
  })
}

function getMockViewingRescheduleHistory(viewing) {
  const history = []

  let previousId = viewing.rescheduled_from_id

  while (previousId) {
    const previous = MOCK_VIEWINGS.find(
      item => String(item.id) === String(previousId)
    )

    if (!previous) {
      break
    }

    history.push({
      duration_min: previous.duration_min,
      end: previous.end,
      id: previous.id,
      start: previous.start,
      status: previous.status,
    })

    previousId = previous.rescheduled_from_id
  }

  return history
}

function getMockViewingDetailPermissions(viewing) {
  const isReadOnly = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.readOnly)

  return {
    ...viewing.permissions,
    can_archive: !isReadOnly && viewing.permissions.can_archive,
    can_edit_content: !isReadOnly,
    can_edit_status: !isReadOnly,
    can_manage: !isReadOnly && viewing.permissions.can_manage,
    can_restore: !isReadOnly && viewing.permissions.can_restore,
    can_view_updates: true,
  }
}

function setMockViewingOutcome({ patch, viewing }) {
  if (!RENTAL_PORTAL_VIEWING_OUTCOME_STATUSES.includes(patch.status)) {
    return
  }

  MOCK_VIEWING_OUTCOME_BY_ID.set(viewing.id, {
    note: patch.outcome_note ?? null,
    recorded_at: new Date().toISOString(),
    recorded_by: MOCK_VIEWING_OUTCOME_ACTOR,
    status: patch.status,
  })
}

function getMockViewingOutcome(viewing) {
  if (!RENTAL_PORTAL_VIEWING_OUTCOME_STATUSES.includes(viewing.status)) {
    return null
  }

  return (
    MOCK_VIEWING_OUTCOME_BY_ID.get(viewing.id) ?? {
      note: null,
      recorded_at: viewing.end,
      recorded_by: viewing.manager_label,
      status: viewing.status,
    }
  )
}

function toMockViewingApplicantEntry({ applicant, isPrimary, label, masked }) {
  return {
    avatar_color: getRentalPortalMockAvatarColor(applicant.id),
    can_view: true,
    id: applicant.id,
    is_primary: isPrimary,
    label,
    masked,
    reference: masked ? toMockApplicantReference(label) : null,
  }
}

function getMockViewingExtraApplicants(viewing) {
  const ids =
    MOCK_VIEWING_EXTRA_APPLICANT_IDS_BY_ID.get(viewing.id) ?? EMPTY_LIST

  return ids
    .map(id => APPLICANTS_SNAPSHOT.find(item => String(item.id) === String(id)))
    .filter(Boolean)
    .map(applicant =>
      toMockViewingApplicantEntry({
        applicant,
        isPrimary: false,
        label: applicant.identity.value,
        masked: false,
      })
    )
}

function getMockViewingApplicantList(viewing) {
  if (!viewing.applicant) {
    return EMPTY_LIST
  }

  const isMasked = viewing.applicant.masked !== false

  return [
    toMockViewingApplicantEntry({
      applicant: viewing.applicant,
      isPrimary: true,
      label: viewing.applicant.label,
      masked: isMasked,
    }),
    ...getMockViewingExtraApplicants(viewing),
  ]
}

function bookMockViewingSlot({ applicantId, viewing }) {
  const applicant = APPLICANTS_SNAPSHOT.find(
    item => String(item.id) === String(applicantId)
  )

  if (!applicant) {
    return
  }

  Object.assign(viewing, {
    applicant: {
      can_view: true,
      id: applicant.id,
      label: applicant.identity.value,
      masked: false,
    },
    applicant_label: applicant.identity.value,
    booked_at: new Date().toISOString(),
    status: MOCK_VIEWING_STATUS_CONFIRMED,
  })
}

export function performMockViewingApplicantsUpdate({
  applicant_ids: applicantIds,
  viewing_id: viewingId,
}) {
  return withMockLatency(RENTAL_PORTAL_MOCK_LATENCY).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_VIEWINGS_WRITE_ERROR)
    }

    const viewing = MOCK_VIEWINGS.find(
      item => String(item.id) === String(viewingId)
    )

    const ids = uniq(applicantIds)

    // TODO: an open slot has no applicant yet, so the first one booked here
    // takes the booking — the endpoint has to do the same server-side
    if (!viewing.applicant && ids.length > 0) {
      bookMockViewingSlot({ applicantId: ids[0], viewing })
    }

    const extraIds = ids.filter(
      id => String(id) !== String(viewing.applicant?.id)
    )

    MOCK_VIEWING_EXTRA_APPLICANT_IDS_BY_ID.set(viewing.id, extraIds)

    return { data: { applicants: getMockViewingApplicantList(viewing) } }
  })
}

export function getMockViewingDetail(viewingId) {
  return withMockLatency(getMockReadDelay(true)).then(() => {
    if (getIsMockReadFailing(false)) {
      return Promise.reject(MOCK_VIEWINGS_READ_ERROR)
    }

    const viewing = MOCK_VIEWINGS.map(toMockServedViewing).find(
      item => String(item.id) === String(viewingId)
    )

    return viewing
      ? {
          data: {
            ...viewing,
            applicants: getMockViewingApplicantList(viewing),
            content: MOCK_VIEWING_NOTE_BY_ID.get(viewing.id) ?? null,
            created: viewing.booked_at ?? viewing.start,
            outcome: getMockViewingOutcome(viewing),
            permissions: getMockViewingDetailPermissions(viewing),
            reschedule_history: getMockViewingRescheduleHistory(viewing),
            updated: viewing.booked_at ?? viewing.start,
          },
        }
      : Promise.reject(MOCK_VIEWINGS_READ_ERROR)
  })
}

export function performMockViewingReschedule({
  duration_min: durationMin,
  start,
  viewing_id: viewingId,
}) {
  return withMockLatency(RENTAL_PORTAL_MOCK_LATENCY).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_VIEWINGS_WRITE_ERROR)
    }

    const viewing = MOCK_VIEWINGS.find(
      item => String(item.id) === String(viewingId)
    )

    if (
      getIsMockViewingOverlapping({
        end: new Date(
          new Date(start).getTime() + durationMin * MINUTES_IN_MS
        ).toISOString(),
        excludeId: viewing.id,
        listingId: viewing.listing_id,
        start,
      })
    ) {
      return Promise.reject({ isOverlap: true })
    }

    const moved = {
      ...toMockBookedViewing({
        end: new Date(
          new Date(start).getTime() + durationMin * MINUTES_IN_MS
        ).toISOString(),
        listingId: viewing.listing_id,
        start,
      }),
      applicant: viewing.applicant,
      applicant_label: viewing.applicant_label,
      manager_label: viewing.manager_label,
      rescheduled_from_id: viewing.id,
      status: MOCK_VIEWING_STATUS_CONFIRMED,
    }

    viewing.status = 'cancelled'

    MOCK_VIEWINGS.push(moved)

    const extraApplicantIds = MOCK_VIEWING_EXTRA_APPLICANT_IDS_BY_ID.get(
      viewing.id
    )

    if (extraApplicantIds) {
      MOCK_VIEWING_EXTRA_APPLICANT_IDS_BY_ID.set(moved.id, extraApplicantIds)
    }

    return { data: { viewing_id: moved.id } }
  })
}

export function patchMockViewing(viewingId, patch) {
  return withMockLatency(RENTAL_PORTAL_MOCK_LATENCY).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_VIEWINGS_WRITE_ERROR)
    }

    const viewing = MOCK_VIEWINGS.find(
      item => String(item.id) === String(viewingId)
    )

    if ('content' in patch) {
      MOCK_VIEWING_NOTE_BY_ID.set(viewing.id, patch.content)
    }

    if (patch.status) {
      viewing.status = patch.status

      setMockViewingOutcome({ patch, viewing })
    }

    return { data: { ...viewing, content: patch.content } }
  })
}

// TODO: stands in for `GET …/viewings/{id}/ics/` (RP-06 API #9) — the file is
// assembled here only until the endpoint lands; the subscribable feed needs the
// server either way (EPIC-RP-06 §14 open-1).
function toMockIcsText(value) {
  return String(value)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
}

function toMockIcsLocalDate({ timezone, value }) {
  return format(toUkTimezoneDate(value, timezone), MOCK_VIEWING_ICS_DATE_FORMAT)
}

function toMockViewingIcs({ timezone, viewing }) {
  const description = [
    viewing.applicant_label && `Applicant: ${viewing.applicant_label}`,
    viewing.manager_label && `Manager: ${viewing.manager_label}`,
  ]
    .filter(Boolean)
    .join('\n')

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//iDWELL//Rental Portal//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:rental-portal-viewing-${viewing.id}@idwell.com`,
    `DTSTAMP:${format(
      toUkTimezoneDate(new Date(), MOCK_VIEWING_ICS_STAMP_TIMEZONE),
      MOCK_VIEWING_ICS_STAMP_FORMAT
    )}`,
    `DTSTART;TZID=${timezone}:${toMockIcsLocalDate({
      timezone,
      value: viewing.start,
    })}`,
    `DTEND;TZID=${timezone}:${toMockIcsLocalDate({
      timezone,
      value: viewing.end,
    })}`,
    `SUMMARY:${toMockIcsText(`Viewing · ${viewing.listing_label}`)}`,
    `LOCATION:${toMockIcsText(viewing.listing_label)}`,
    ...(description
      ? [`DESCRIPTION:${toMockIcsText(description)}`]
      : EMPTY_LIST),
    'END:VEVENT',
    'END:VCALENDAR',
  ].join(MOCK_VIEWING_ICS_LINE_BREAK)
}

export function downloadMockViewingIcs({ timezone, viewing }) {
  const url = URL.createObjectURL(
    new Blob([toMockViewingIcs({ timezone, viewing })], {
      type: MOCK_VIEWING_ICS_MIME,
    })
  )

  const anchor = document.createElement('a')

  anchor.href = url
  anchor.download = `viewing-${viewing.id}.ics`

  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()

  URL.revokeObjectURL(url)
}

export function getMockListingOpenSlots({ listing_id: listingId }) {
  return withMockLatency(getMockReadDelay(true)).then(() => {
    if (getIsMockReadFailing(false)) {
      return Promise.reject(MOCK_VIEWINGS_READ_ERROR)
    }

    const items = getMockActiveViewings()
      .filter(
        viewing =>
          String(viewing.listing_id) === String(listingId) &&
          viewing.status === RENTAL_PORTAL_VIEWING_STATUS_SLOT_OPEN &&
          !getIsViewingPast({ timezone: DEFAULT_TIME_ZONE, viewing })
      )
      .map(viewing => ({
        duration_min: viewing.duration_min,
        end: viewing.end,
        listing_id: viewing.listing_id,
        slot_id: viewing.id,
        start: viewing.start,
      }))
      .sort((left, right) => left.start.localeCompare(right.start))

    return {
      data: {
        items,
        meta: { count: items.length, curr_page: 1, page_count: 1 },
      },
    }
  })
}

export function performMockViewingsBook({
  applicant_ids: applicantIds,
  end,
  listing_id: listingId,
  slot_id: slotId,
  start,
}) {
  return withMockLatency(RENTAL_PORTAL_MOCK_LATENCY).then(() => {
    if (
      slotId &&
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.slotTaken)
    ) {
      return Promise.reject(MOCK_VIEWING_SLOT_TAKEN_ERROR)
    }

    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_VIEWINGS_WRITE_ERROR)
    }

    const [primaryId, ...extraIds] = uniq(applicantIds)

    const slot = MOCK_VIEWINGS.find(
      viewing =>
        viewing.status === RENTAL_PORTAL_VIEWING_STATUS_SLOT_OPEN &&
        !viewing.archived_at &&
        (slotId
          ? String(viewing.id) === String(slotId)
          : String(viewing.listing_id) === String(listingId) &&
            viewing.start === start)
    )

    if (slotId && !slot) {
      return Promise.reject(MOCK_VIEWING_SLOT_TAKEN_ERROR)
    }

    if (!slot && getIsMockViewingOverlapping({ end, listingId, start })) {
      return Promise.reject({ isOverlap: true })
    }

    const booked = slot ?? toMockBookedViewing({ end, listingId, start })

    bookMockViewingSlot({ applicantId: primaryId, viewing: booked })

    MOCK_VIEWING_EXTRA_APPLICANT_IDS_BY_ID.set(booked.id, extraIds)

    if (!slot) {
      MOCK_VIEWINGS.push(booked)
    }

    return {
      data: { status: MOCK_VIEWING_STATUS_CONFIRMED, viewing_id: booked.id },
    }
  })
}

export function getMockViewingsDefaultColumns() {
  return Promise.resolve(getViewingsColumns())
}

export function getMockViewingsPage({
  filterValues,
  isInitial,
  ordering,
  page,
  pageSize,
}) {
  return withMockLatency(getMockReadDelay(isInitial)).then(() => {
    if (getIsMockReadFailing(isInitial)) {
      return Promise.reject(MOCK_VIEWINGS_READ_ERROR)
    }

    const allItems = getSortedMockViewings(
      getFilteredMockViewings(getMockViewingsSource(), filterValues),
      ordering
    )

    const count = allItems.length
    const pageCount = Math.ceil(count / pageSize)
    const safePage = Math.min(page, Math.max(1, pageCount))

    return {
      allItems,
      items: {
        items: getPagedMockViewings(allItems, { page: safePage, pageSize }),
        permissions: MOCK_VIEWINGS_PERMISSIONS,
      },
      pagination: { count, page: safePage, pageCount, pageSize },
    }
  })
}

export function getMockViewingsRange({ filterValues, from, isInitial, to }) {
  return withMockLatency(getMockReadDelay(isInitial)).then(() => {
    if (getIsMockReadFailing(isInitial)) {
      return Promise.reject(MOCK_VIEWINGS_READ_ERROR)
    }

    const items = getFilteredMockViewings(
      getMockViewingsSource(),
      filterValues
    ).filter(viewing => {
      const day = viewing.start.slice(0, 10)

      return day >= from && day <= to
    })

    return { items }
  })
}

export function getMockArchivedViewings() {
  return MOCK_VIEWINGS.filter(viewing => viewing.archived)
}

export function setMockViewingArchived(id, archived) {
  const index = MOCK_VIEWINGS.findIndex(viewing => viewing.id === id)

  if (index !== -1) {
    MOCK_VIEWINGS[index] = {
      ...MOCK_VIEWINGS[index],
      archived,
      archived_at: archived ? new Date().toISOString() : null,
      permissions: {
        ...MOCK_VIEWINGS[index].permissions,
        can_archive: !archived,
        can_restore: archived,
      },
    }
  }
}

export function performMockViewingArchive({ archived, id }) {
  return withMockLatency(RENTAL_PORTAL_MOCK_LATENCY).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_VIEWINGS_WRITE_ERROR)
    }

    setMockViewingArchived(id, archived)

    return { data: { archived, id } }
  })
}

function getMockViewingsMassActionTargets({ all, filters }) {
  const { archived, exclude_id: excludeId, id: ids, ...facets } = filters

  const scoped = getFilteredMockViewings(
    archived ? getMockArchivedViewings() : getMockActiveViewings(),
    facets
  )

  return all
    ? scoped.filter(viewing => !excludeId.includes(viewing.id))
    : scoped.filter(viewing => ids.includes(viewing.id))
}

function getMockViewingsMassActionStatusCode({ action, index, viewing }) {
  const isEligible =
    action === VIEWING_MASS_ACTION.restore
      ? viewing.permissions.can_restore
      : viewing.permissions.can_archive

  if (!isEligible) {
    return HTTP_STATUS_FORBIDDEN
  }

  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.massActionPartial) &&
    index % 2 === 1
    ? HTTP_STATUS_ERROR
    : HTTP_STATUS_OK
}

// TODO: the epic declares no `viewings/mass-action/` endpoint — the request and
// response shapes mirror the listings mock until one exists
export function performMockViewingsMassAction(request) {
  console.debug('[RP mock] POST viewings/mass-action/', request)

  return withMockLatency(RENTAL_PORTAL_MOCK_LATENCY).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.massActionError)) {
      return Promise.reject(MOCK_VIEWINGS_WRITE_ERROR)
    }

    const { action, all, filters } = request

    const items = getMockViewingsMassActionTargets({ all, filters }).map(
      (viewing, index) => ({
        id: viewing.id,
        status_code: getMockViewingsMassActionStatusCode({
          action,
          index,
          viewing,
        }),
      })
    )

    const successIds = items
      .filter(item => isApiSuccessStatus(item.status_code))
      .map(item => item.id)

    successIds.forEach(id =>
      setMockViewingArchived(id, action !== VIEWING_MASS_ACTION.restore)
    )

    return {
      data: { items, success_ids: successIds },
      status: HTTP_STATUS_OK,
    }
  })
}

const MOCK_VIEWING_LISTING_LABEL = new Map(
  LISTINGS_SNAPSHOT.map(listing => [listing.id, listing.unit_label])
)

function getIsMockViewingOverlapping({ end, excludeId, listingId, start }) {
  return getMockActiveViewings().some(
    viewing =>
      viewing.listing_id === listingId &&
      String(viewing.id) !== String(excludeId) &&
      viewing.status !== RENTAL_PORTAL_VIEWING_STATUS.cancelled &&
      viewing.start < end &&
      start < viewing.end
  )
}

function getMockViewingSeriesStarts({ repeatUntil, start }) {
  if (!repeatUntil) {
    return [start]
  }

  const until = new Date(repeatUntil)

  const starts = []

  let current = new Date(start)

  while (!isAfter(current, until)) {
    starts.push(current.toISOString())

    current = addWeeks(current, 1)
  }

  return starts
}

// TODO: the overlap response is the mock's own — the epic names the endpoint
// but not its error shape.
export function performMockViewingsCreate({
  duration_min: durationMin,
  listing_id: listingId,
  repeat_until: repeatUntil,
  start,
}) {
  console.debug('[RP mock] POST viewings/', {
    durationMin,
    listingId,
    repeatUntil,
    start,
  })

  return withMockLatency(RENTAL_PORTAL_MOCK_LATENCY).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_VIEWINGS_WRITE_ERROR)
    }

    const slots = getMockViewingSeriesStarts({ repeatUntil, start }).map(
      seriesStart => ({
        end: new Date(
          new Date(seriesStart).getTime() + durationMin * MINUTES_IN_MS
        ).toISOString(),
        start: seriesStart,
      })
    )

    const overlapping = slots.find(slot =>
      getIsMockViewingOverlapping({
        end: slot.end,
        listingId,
        start: slot.start,
      })
    )

    if (overlapping) {
      return Promise.reject({ isOverlap: true })
    }

    let nextId = Math.max(...MOCK_VIEWINGS.map(viewing => viewing.id)) + 1

    slots.forEach(slot => {
      MOCK_VIEWINGS.push({
        applicant: null,
        applicant_label: null,
        archived: false,
        archived_at: null,
        booked_at: null,
        duration_min: durationMin,
        end: slot.end,
        flat: MOCK_VIEWING_LISTING_FLAT.get(listingId) ?? null,
        id: nextId,
        listing_id: listingId,
        listing_label: MOCK_VIEWING_LISTING_LABEL.get(listingId) ?? null,
        manager_label: MOCK_VIEWINGS_MANAGER_LABEL,
        permissions: {
          can_archive: true,
          can_manage: true,
          can_restore: false,
          can_view: true,
        },
        source: 'manager',
        start: slot.start,
        status: RENTAL_PORTAL_VIEWING_STATUS_SLOT_OPEN,
        uk_id: 1,
      })

      nextId += 1
    })

    return { data: { created_count: slots.length } }
  })
}
